import { useNavigate } from "react-router-dom";
import { Alert, Button, Icon, Modal, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";

interface DowngradeModalData {
  planName: string;
  currentVehicles: number;
  allowedVehicles: number;
  currentDrivers: number;
  allowedDrivers: number;
  exceedsVehicles: boolean;
  exceedsDrivers: boolean;
}

interface CannotDowngradeModalProps {
  open: boolean;
  onClose: () => void;
  data: DowngradeModalData;
  title?: string;
  onParentClose?: () => void;
}

export const CannotDowngradeModal = ({
  open,
  onClose,
  data: { planName, currentVehicles, allowedVehicles, currentDrivers, allowedDrivers, exceedsVehicles, exceedsDrivers },
  title = "Can't downgrade yet",
  onParentClose,
}: CannotDowngradeModalProps) => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();

  const handleGoToVehicles = () => {
    onClose();
    onParentClose?.();
    navigate(`/operator/${operatorId}/vehicles/my-vehicles`);
  };

  const handleGoToDrivers = () => {
    onClose();
    onParentClose?.();
    navigate(`/operator/${operatorId}/drivers/my-drivers`);
  };

  const handleNotNow = () => {
    onClose();
  };

  // Determine which scenario we're in
  const isBothExceeded = exceedsVehicles && exceedsDrivers;
  const isVehiclesOnly = exceedsVehicles && !exceedsDrivers;
  const isDriversOnly = !exceedsVehicles && exceedsDrivers;

  const warningIcon = (
    <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
      <div className="absolute inset-[-4px] rounded-full border-8 border-danger-light" />
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger">
        <Icon name="Danger" size="lg" className="text-white" />
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] md:w-[480px] lg:w-[480px]" featuredIcon={warningIcon}>
      <div className="flex flex-col gap-5">
        {/* Title and Description */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2 text-neutral-dark-gray">
            <Typography variant="title">{title}</Typography>
            {isBothExceeded ? (
              <Typography variant="paragraph">
                Your account exceeds the limits for the <span className="font-semibold">{planName}</span>.
              </Typography>
            ) : (
              <Typography variant="paragraph">
                Your account currently has <span className="font-semibold">{isVehiclesOnly ? currentVehicles : currentDrivers}</span>{" "}
                {isVehiclesOnly ? "vehicles" : "drivers"}, but the <span className="font-semibold">{planName} Plan</span> allows{" "}
                <span className="font-semibold">{isVehiclesOnly ? allowedVehicles : allowedDrivers}</span>{" "}
                {isVehiclesOnly ? (allowedVehicles === 1 ? "vehicle" : "vehicles") : allowedDrivers === 1 ? "driver" : "drivers"}.{" "}
                {isVehiclesOnly ? "Archive vehicles" : "Remove drivers"} first, then try again.
              </Typography>
            )}
          </div>
        </div>

        {/* Current Status */}
        {isBothExceeded && (
          <div className="text-neutral-dark-gray">
            <ul className="flex flex-col gap-1 pl-4">
              <li className="list-disc">
                <Typography variant="paragraph">
                  <span className="font-semibold">Vehicles:</span> {currentVehicles}/{allowedVehicles} ({allowedVehicles} allowed)
                </Typography>
              </li>
              <li className="list-disc">
                <Typography variant="paragraph">
                  <span className="font-semibold">Drivers:</span> {currentDrivers}/{allowedDrivers} ({allowedDrivers} allowed)
                </Typography>
              </li>
            </ul>
          </div>
        )}

        {isBothExceeded && (
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Please update your account to meet the plan limits before downgrading.
          </Typography>
        )}

        {/* What you need to do */}
        <div className="bg-neutral-pale-gray rounded-lg border border-neutral-mid-gray p-4">
          <Typography variant="title" className="text-neutral-dark-gray">
            What you need to do
          </Typography>
          <ol className="mt-4 flex flex-col pl-5 text-neutral-dark-gray">
            {(isBothExceeded || isVehiclesOnly) && (
              <li className="list-decimal">
                <Typography variant="paragraph">Go to Vehicles → My Vehicles{isBothExceeded ? " and archive vehicles" : ""}</Typography>
              </li>
            )}
            {isVehiclesOnly && (
              <li className="list-decimal">
                <Typography variant="paragraph">Archive vehicle{currentVehicles > 1 ? "s" : ""} to meet the plan limit</Typography>
              </li>
            )}
            {(isBothExceeded || isDriversOnly) && (
              <li className="list-decimal">
                <Typography variant="paragraph">Go to Drivers → My Drivers{isBothExceeded ? " and remove drivers" : ""}</Typography>
              </li>
            )}
            {isDriversOnly && (
              <li className="list-decimal">
                <Typography variant="paragraph">Remove driver{currentDrivers > 1 ? "s" : ""} to meet the plan limit</Typography>
              </li>
            )}
            <li className="list-decimal">
              <Typography variant="paragraph">Come back here and downgrade again</Typography>
            </li>
          </ol>
        </div>

        {/* Important Alert */}
        <Alert
          type="info"
          className="border border-info"
          message={
            <div className="flex flex-col gap-1">
              <Typography variant="action" className="font-semibold text-info">
                Important
              </Typography>
              <Typography variant="paragraph" className="mt-2 font-normal text-info">
                {isBothExceeded && "Before updating vehicles or drivers, double-check upcoming trips."}
                {isVehiclesOnly &&
                  "Before archiving, double-check upcoming trips. If a vehicle is assigned to a trip, update the trip to use a different vehicle (or remove the vehicle from the trip) before archiving and downgrading."}
                {isDriversOnly &&
                  "Before removing or deactivating a driver, double-check upcoming trips. If a driver is assigned to a trip, reassign the trip to another driver before removing them."}
              </Typography>
              {isBothExceeded && (
                <ul className="mt-2 flex flex-col gap-1 pl-4">
                  <li className="list-disc">
                    <Typography variant="paragraph" className="font-normal text-info">
                      If a vehicle is assigned to a trip, update the trip to use a different vehicle before archiving.
                    </Typography>
                  </li>
                  <li className="list-disc">
                    <Typography variant="paragraph" className="font-normal text-info">
                      If a driver is assigned to a trip, reassign the trip to another driver before removing or deactivating them.
                    </Typography>
                  </li>
                </ul>
              )}
            </div>
          }
          noIcon
        />

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          {(isBothExceeded || isVehiclesOnly) && (
            <Button type="button" size="lg" variant="primary" onClick={handleGoToVehicles} className="w-full">
              Go to My Vehicles
            </Button>
          )}
          {(isBothExceeded || isDriversOnly) && (
            <Button type="button" size="lg" variant="primary" onClick={handleGoToDrivers} className="w-full">
              Go to My Drivers
            </Button>
          )}
          <Button type="button" size="lg" variant="secondary" onClick={handleNotNow} className="w-full">
            Not Now
          </Button>
        </div>
      </div>
    </Modal>
  );
};
